import { WhatsAppCloudSignupSession } from '../types/whatsAppCloudSignup';

/**
 * Meta's Embedded Signup hands back its two halves through different channels: the authorization
 * code arrives in the `FB.login` callback, while the account the owner picked is posted to the
 * opener as a `WA_EMBEDDED_SIGNUP` message. Both are needed before the backend can finish.
 */
export interface WhatsAppEmbeddedSignupResult {
  code: string;
  whatsAppAccountId: string;
  phoneNumberId: string | null;
}

export class WhatsAppEmbeddedSignupCancelled extends Error {
  constructor() {
    super('The Meta signup was closed before it finished.');
    this.name = 'WhatsAppEmbeddedSignupCancelled';
  }
}

interface FacebookLoginResponse {
  authResponse?: { code?: string } | null;
  status?: string;
}

interface FacebookSdk {
  init: (options: Record<string, unknown>) => void;
  login: (
    callback: (response: FacebookLoginResponse) => void,
    options: Record<string, unknown>,
  ) => void;
}

declare global {
  interface Window {
    FB?: FacebookSdk;
    fbAsyncInit?: () => void;
  }
}

const SDK_ELEMENT_ID = 'facebook-jssdk';
const SIGNUP_MESSAGE_ORIGINS = ['https://www.facebook.com', 'https://web.facebook.com'];

let sdkPromise: Promise<FacebookSdk> | null = null;
let initializedFor: string | null = null;

const loadSdkScript = (locale: string): Promise<FacebookSdk> => {
  if (sdkPromise) {
    return sdkPromise;
  }

  sdkPromise = new Promise<FacebookSdk>((resolve, reject) => {
    if (window.FB) {
      resolve(window.FB);
      return;
    }

    const existing = document.getElementById(SDK_ELEMENT_ID);
    if (existing) {
      existing.addEventListener('load', () => {
        if (window.FB) {
          resolve(window.FB);
        } else {
          reject(new Error('The Facebook SDK loaded without exposing FB.'));
        }
      });
      existing.addEventListener('error', () => reject(new Error('The Facebook SDK could not be loaded.')));
      return;
    }

    const script = document.createElement('script');
    script.id = SDK_ELEMENT_ID;
    script.src = `https://connect.facebook.net/${encodeURIComponent(locale)}/sdk.js`;
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      if (window.FB) {
        resolve(window.FB);
      } else {
        reject(new Error('The Facebook SDK loaded without exposing FB.'));
      }
    };
    script.onerror = () => {
      // A failed load must not poison later attempts; the owner may just be offline for a moment.
      sdkPromise = null;
      reject(new Error('The Facebook SDK could not be loaded.'));
    };

    document.body.appendChild(script);
  });

  return sdkPromise;
};

const ensureSdk = async (session: WhatsAppCloudSignupSession): Promise<FacebookSdk> => {
  const sdk = await loadSdkScript(session.sdkLocale || 'en_US');
  const signature = `${session.appId}|${session.graphApiVersion}`;

  if (initializedFor !== signature) {
    sdk.init({
      appId: session.appId,
      autoLogAppEvents: true,
      xfbml: false,
      version: session.graphApiVersion,
    });
    initializedFor = signature;
  }

  return sdk;
};

export const launchWhatsAppEmbeddedSignup = (
  session: WhatsAppCloudSignupSession,
): Promise<WhatsAppEmbeddedSignupResult> => new Promise((resolve, reject) => {
  let whatsAppAccountId: string | null = null;
  let phoneNumberId: string | null = null;
  let settled = false;

  const finish = (outcome: () => void) => {
    if (settled) {
      return;
    }

    settled = true;
    window.removeEventListener('message', onMessage);
    outcome();
  };

  function onMessage(event: MessageEvent) {
    if (!SIGNUP_MESSAGE_ORIGINS.includes(event.origin)) {
      return;
    }

    let payload: Record<string, any>;
    try {
      payload = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    } catch {
      return;
    }

    if (!payload || payload.type !== 'WA_EMBEDDED_SIGNUP') {
      return;
    }

    if (payload.event === 'FINISH' || payload.event === 'FINISH_ONLY_WABA') {
      whatsAppAccountId = payload.data?.waba_id ?? whatsAppAccountId;
      phoneNumberId = payload.data?.phone_number_id ?? phoneNumberId;
      return;
    }

    if (payload.event === 'CANCEL') {
      finish(() => reject(new WhatsAppEmbeddedSignupCancelled()));
      return;
    }

    if (payload.event === 'ERROR') {
      const message = payload.data?.error_message;
      finish(() => reject(new Error(typeof message === 'string' && message
        ? message
        : 'Meta reported an error during signup.')));
    }
  }

  window.addEventListener('message', onMessage);

  ensureSdk(session)
    .then(sdk => {
      sdk.login(
        response => {
          const code = response?.authResponse?.code;
          if (!code) {
            finish(() => reject(new WhatsAppEmbeddedSignupCancelled()));
            return;
          }

          if (!whatsAppAccountId) {
            finish(() => reject(new Error('The signup finished without a WhatsApp Business Account.')));
            return;
          }

          finish(() => resolve({ code, whatsAppAccountId: whatsAppAccountId!, phoneNumberId }));
        },
        {
          config_id: session.configId,
          response_type: 'code',
          override_default_response_type: true,
          extras: {
            setup: {},
            featureType: '',
            sessionInfoVersion: '3',
          },
        },
      );
    })
    .catch(error => finish(() => reject(error)));
});
